import {
  get,
  compact,
  flatMap,
  map,
  reduce,
  isEmpty,
  mapValues,
  forEach,
  times,
  uniqueId,
  memoize,
  head,
  find,
} from 'lodash';

import { BREADCRUMB_NAVIGATION_LINK_TYPES } from 'constants/breadcrumbNavigation';
import { COLLECTION_COLORWAY_PICKER_ITEM_TYPES, COLLECTION_TYPES } from 'constants/collectionPage';
import CatalogSelector from 'state/selectors/catalog';
import BreadcrumbsUtil from 'utils/Breadcrumbs';
import Filters, { FILTER_IDENTIFIERS } from 'utils/Filters';
import Log from 'utils/Log';

const ENABLE_CACHE = true;

class CollectionPage {
  /**
   * This method returns a unique cacheKey unless cache is enabled and a cacheKey has been provided
   */
  static _resolveCache = ({ cacheKey }) => {
    if (!ENABLE_CACHE || !cacheKey) {
      return uniqueId();
    }

    return cacheKey;
  };

  /**
   * Returns the handle of the collection
   */
  static getCollectionHandle = state => get(state, ['collectionPage', 'handle']);

  /**
   * Returns the type of the collection
   */
  static getCollectionType = state => get(state, ['collectionPage', 'type']);

  /**
   * Returns a cache key specific to the current collection, and the current applied filters
   */
  static getCollectionFilterCacheKey = (state, prefix = '') => {
    const options = CollectionPage.getAppliedFilterOptions(state);

    return reduce(
      options,
      (string, option) => `${string}__${option.filterIdentifier}:${option.option}`,
      prefix
    );
  };

  /**
   * Returns the value of a specific filter option
   */
  static getFilterOptionValue = (state, { filterIdentifier, option }) =>
    get(state, ['collectionPage', 'filters', filterIdentifier, 'options', option, 'value'], false);

  /**
   * Returns true if the corresponding filter is expanded. Returns false otherwise.
   */
  static isFilterExpanded = (state, { filterIdentifier }) =>
    get(state, ['collectionPage', 'filters', filterIdentifier, 'isExpanded'], false);

  /**
   * Returns an array containing the currently applied filter options
   */
  static getAppliedFilterOptions = state => {
    return compact(
      flatMap(get(state, ['collectionPage', 'filters']), ({ options }, filterIdentifier) =>
        map(options, ({ value }, option) => {
          if (value) {
            let label;

            if (filterIdentifier === FILTER_IDENTIFIERS.SIZE) {
              label = CatalogSelector.getFirstMatchingFilterLabel(state, option);
            }

            return { filterIdentifier, option, label, value };
          }

          return null;
        })
      )
    );
  };

  /**
   * Returns true if there is at least one applied filter option. Returns false otherwise
   */
  static hasAppliedFilterOption = state => {
    return !isEmpty(CollectionPage.getAppliedFilterOptions(state));
  };

  /**
   * Returns a list of filterItems to be consumed by the Filtering Engine, based on the currently applied filter options
   */
  static getFilteringEngineFilterItems = state => {
    const appliedFilters = CollectionPage.getAppliedFilterOptions(state);

    const filterMap = {};

    forEach(appliedFilters, ({ filterIdentifier, option }) => {
      if (!filterMap[filterIdentifier]) {
        filterMap[filterIdentifier] = [];
      }

      filterMap[filterIdentifier].push(option);
    });

    const filterItems = mapValues(filterMap, (valuesArray, filterIdentifier) => ({
      configuration: Filters.getFilterConfiguration({ filterIdentifier }),
      values: valuesArray,
    }));

    return filterItems;
  };

  /**
   * Returns true if the filter drawer is open. Returns false otherwise
   */
  static getIsFilterDrawerOpen = state =>
    get(state, ['collectionPage', 'isFilterDrawerOpen'], false);

  /**
   * Returns a 2D array of carousel slides, that are filled with items according to the following business logic:
   *  - Each slide has a set number of items (i.e. nItemsPerSlide)
   *  - Left navigation arrow is never shown on first slide
   *  - Right navigation arrow is only shown if there is a next slide
   *  - Slides should be consolidated to avoid having a slide with only one colorway
   */
  static _buildColorwayPickerSlides = ({ colorways, nItemsPerSlide = 6, cacheKey }) => {
    Log.debug(`_executeFilters - uncached (${cacheKey})`);

    if (nItemsPerSlide < 3) {
      throw new Error(`There needs to be at least 3 items per slide, found ${nItemsPerSlide}.`);
    }

    // Init
    const slides = [];
    let currentSlide;
    let remainingColorways = [...colorways];

    // Helpers
    const startNewSlide = () => {
      currentSlide = [];
      slides.push(currentSlide);
    };

    const placeColorwayItem = data => {
      currentSlide.push({ type: COLLECTION_COLORWAY_PICKER_ITEM_TYPES.COLORWAY, data });
    };

    const placeLeftNavigationArrow = () => {
      currentSlide.push({ type: COLLECTION_COLORWAY_PICKER_ITEM_TYPES.NAVIGATION_ARROW_PREV });
    };

    const placeRightNavigationArrow = () => {
      currentSlide.push({ type: COLLECTION_COLORWAY_PICKER_ITEM_TYPES.NAVIGATION_ARROW_NEXT });
    };

    const placeEmptyItem = () => {
      currentSlide.push({ type: COLLECTION_COLORWAY_PICKER_ITEM_TYPES.EMPTY });
    };

    // Start first slide
    startNewSlide();

    // Iterate over remaining colorways until there are no more
    while (remainingColorways.length > 0) {
      // Take first colorway in list of remaining colorways
      const [currentColorway, ...otherColorways] = remainingColorways;

      // Figure out how many slots are left on current slide
      const slotsLeftOnCurrentSlide = nItemsPerSlide - currentSlide.length;

      // If only one slot left on current slide, either:
      // - place last remaining item on current slide if only one remaining colorway
      // or
      // - fill remaining slot with a right pagination arrow and place remaining items on new slide if more than one remaining colorway
      if (slotsLeftOnCurrentSlide === 1) {
        if (remainingColorways.length === 1) {
          placeColorwayItem(currentColorway);
        } else {
          placeRightNavigationArrow();
          startNewSlide();
          placeLeftNavigationArrow();
          placeColorwayItem(currentColorway);
        }
      } else {
        // Else the current slide still has plenty of space, so just place the current colorway without worrying about pagination
        placeColorwayItem(currentColorway);
      }

      // Shift the array of remaining colors. This removes the current colorway from the list of remaining items.
      remainingColorways = otherColorways;

      // Once all items have been placed on slides, fill up any slots left on current slide with empty items
      if (remainingColorways.length === 0) {
        times(slotsLeftOnCurrentSlide - 1, placeEmptyItem);
      }
    }

    return slides;
  };

  static buildColorwayPickerSlides = memoize(
    CollectionPage._buildColorwayPickerSlides,
    CollectionPage._resolveCache
  );

  static getCollectionItems = (state, masterProduct) => {
    let collectionItems = map(masterProduct, masterProductHandle => {
      const availableMasterProductColorways = CatalogSelector.getAvailableMasterProductColorways(
        state,
        masterProductHandle
      );

      // love the chaching
      const masterProductCacheKey = CatalogSelector.getMasterProductCacheKey(
        state,
        masterProductHandle
      );
      const masterProductFilterCacheKey = CollectionPage.getCollectionFilterCacheKey(
        state,
        masterProductCacheKey
      );

      const filteredMasterProductColorways = Filters.executeFilters({
        items: availableMasterProductColorways,
        filterItems: CollectionPage.getFilteringEngineFilterItems(state),
        cacheKey: masterProductFilterCacheKey,
      });
      const representativeColorway = head(filteredMasterProductColorways);

      if (!representativeColorway) {
        return null;
      }

      return representativeColorway;
    });

    collectionItems = compact(collectionItems);

    return collectionItems;
  };

  static getCollectionMetadata = (state, collectionHandle, parentCollection) => {
    const collectionType = CollectionPage.getCollectionType(state);
    const collectionItems = CollectionPage.getCollectionItems(
      state,
      parentCollection.masterProducts
    );
    const { breadcrumbTrail } = BreadcrumbsUtil.getBreadcrumbTrail({
      type: BREADCRUMB_NAVIGATION_LINK_TYPES.COLLECTION,
      handle: collectionHandle,
    });
    const collectionTopParent = find(breadcrumbTrail, {
      type: BREADCRUMB_NAVIGATION_LINK_TYPES.COLLECTION,
    });

    const collectionInfo = {
      collection_handle: collectionHandle,
      collection_type: collectionType,
      collection_name: parentCollection.name,
      collection_url: window.location.href,
      collection_style_count: collectionItems.length,
      is_markdown_collection: parentCollection.isSaleCollection,
      collection_parent: collectionTopParent?.label,
      list_id: parentCollection.cfId,
    };

    return collectionInfo;
  };

  static getCustomCollectionMetadata(state, collectionLength) {
    return {
      collection_handle: state?.collection?.customCollection?.handle,
      collection_type: COLLECTION_TYPES.CUSTOM_COLLECTION,
      collection_name: state?.collection?.customCollection?.title,
      collection_url: window.location.href,
      collection_style_count: collectionLength,
    };
  }
}

export default CollectionPage;
